import {
  ApiResponse,
  Feedback,
  FeedbackQuestion,
  FeedbackResponseEntry,
  TicketManagerTicket,
  TicketManagerTicketDetail,
} from '@vision/ui/interfaces';
import {
  normalizeTicketManagerTicketStatus,
  TicketManagerTicketStatusResponseDto,
} from './ticket-manager/statuses/ticket-statuses.mappers';

export { normalizeTicketManagerTicketStatus };
export type { TicketManagerTicketStatusResponseDto };

export interface TicketManagerTicketOwnerApi {
  full_name?: string;
  id?: string;
  name?: string;
  surname?: string;
  type?: string;
}

export interface TicketManagerTicketAssigneeUserApi {
  email?: string;
  full_name?: string;
  id?: string;
  name?: string;
  surname?: string;
}

export interface TicketManagerTicketAssigneeApi {
  user?: TicketManagerTicketAssigneeUserApi | null;
}

export interface TicketManagerTicketDetailCustomerApi {
  id?: string;
  name?: string | null;
}

export interface TicketManagerTicketDetailFollowUpSurveyApi {
  feedback_id?: string | null;
  flow_id?: string;
  url?: string;
}

export interface TicketManagerTicketDetailPermissionScopeApi {
  role?: string | null;
}

export interface TicketManagerTicketDetailRootCauseApi {
  id?: string | null;
  name?: string | null;
}

export interface TicketManagerTicketDetailCustomFieldApi {
  field_code: string;
  field_name: string;
  value: string;
  visible: boolean;
}

export interface TicketManagerTicketDetailMetaApi {
  root_cause_group_id?: string | null;
}

export interface TicketManagerTicketDetailApi {
  assignee?: TicketManagerTicketAssigneeApi | string | null;
  assignee_id?: string | null;
  assignee_name?: string | null;
  created_at: string;
  custom_fields?: TicketManagerTicketDetailCustomFieldApi[] | null;
  customer_feedback?: Feedback | null;
  customer?: TicketManagerTicketDetailCustomerApi | null;
  description?: string | null;
  follow_up_feedback?: Feedback | Feedback[] | null;
  follow_up_survey?: TicketManagerTicketDetailFollowUpSurveyApi | null;
  id: string;
  meta?: TicketManagerTicketDetailMetaApi | null;
  name?: string | null;
  owner?: TicketManagerTicketOwnerApi | string | null;
  owner_id?: string | null;
  owner_name?: string | null;
  owner_type?: string | null;
  parent_ticket_id?: string | null;
  permission_scope?: TicketManagerTicketDetailPermissionScopeApi | null;
  priority: TicketManagerTicketDetail['priority'];
  ref_id?: number | string | null;
  root_causes?: TicketManagerTicketDetailRootCauseApi[] | null;
  slug?: string | null;
  source_id?: string | null;
  source_type?: string | null;
  status?: TicketManagerTicketStatusResponseDto | null;
  ticket_pool_id?: string | null;
  ticket_project_id?: string | null;
  updated_at: string;
}

export interface TicketManagerTicketApi {
  created_at: string;
  customer_name?: string;
  id: string;
  name?: string;
  owner?: TicketManagerTicketOwnerApi | string | null;
  owner_name?: string;
  priority: TicketManagerTicket['priority'];
  ref_id?: number | string;
  slug?: string | null;
  status?: TicketManagerTicketStatusResponseDto | null;
  ticket_pool_id?: string;
  ticket_queue_id?: string | null;
  ticket_no?: string;
  updated_at: string;
}

function normalizeTicketOwnerName(
  ticket: TicketManagerTicketApi | TicketManagerTicket | TicketManagerTicketDetailApi | TicketManagerTicketDetail,
): string {
  if ('owner_name' in ticket && typeof ticket.owner_name === 'string') {
    return ticket.owner_name;
  }

  if ('owner' in ticket) {
    if (typeof ticket.owner === 'string') {
      return ticket.owner;
    }

    if (ticket.owner && typeof ticket.owner === 'object') {
      return ticket.owner.full_name || [ticket.owner.name, ticket.owner.surname].filter(Boolean).join(' ');
    }
  }

  return '';
}

function normalizeTicketOwnerId(ticket: TicketManagerTicketDetailApi | TicketManagerTicketDetail): string {
  if ('owner_id' in ticket && typeof ticket.owner_id === 'string') {
    return ticket.owner_id;
  }

  if ('owner' in ticket && ticket.owner && typeof ticket.owner === 'object') {
    return ticket.owner.id || '';
  }

  return '';
}

function normalizeTicketOwnerType(ticket: TicketManagerTicketDetailApi | TicketManagerTicketDetail): string {
  if ('owner_type' in ticket && typeof ticket.owner_type === 'string') {
    return ticket.owner_type;
  }

  if ('owner' in ticket && ticket.owner && typeof ticket.owner === 'object') {
    return ticket.owner.type || '';
  }

  return '';
}

function normalizeTicketAssigneeId(ticket: TicketManagerTicketDetailApi | TicketManagerTicketDetail): string {
  if ('assignee_id' in ticket && typeof ticket.assignee_id === 'string') {
    return ticket.assignee_id;
  }

  if ('assignee' in ticket && ticket.assignee && typeof ticket.assignee === 'object') {
    return ticket.assignee.user?.id || '';
  }

  return '';
}

function normalizeTicketAssigneeName(ticket: TicketManagerTicketDetailApi | TicketManagerTicketDetail): string {
  if ('assignee_name' in ticket && typeof ticket.assignee_name === 'string') {
    return ticket.assignee_name;
  }

  if ('assignee' in ticket) {
    if (typeof ticket.assignee === 'string') {
      return ticket.assignee;
    }

    if (ticket.assignee && typeof ticket.assignee === 'object') {
      const assigneeUser = ticket.assignee.user;

      return (
        assigneeUser?.full_name ||
        [assigneeUser?.name, assigneeUser?.surname].filter(Boolean).join(' ') ||
        assigneeUser?.email ||
        ''
      );
    }
  }

  return '';
}

function normalizeTicketNumber(
  ticket: Pick<TicketManagerTicketApi | TicketManagerTicketDetailApi, 'id' | 'ref_id'> & {
    slug?: string | null;
  },
): string {
  return (
    ticket.slug ||
    (ticket.ref_id !== undefined && ticket.ref_id !== null && ticket.ref_id !== '' ? String(ticket.ref_id) : ticket.id)
  );
}

function normalizeFeedbackResponseEntry(response: FeedbackResponseEntry): FeedbackResponseEntry {
  if (Array.isArray(response)) {
    return response.map((item) => ({
      id: item?.id || '',
      positivity: item?.positivity ?? null,
      question: (item?.question as FeedbackQuestion | null | undefined) || null,
      response: item?.response || '',
    }));
  }

  return {
    id: response?.id || '',
    positivity: response?.positivity ?? null,
    question: (response?.question as FeedbackQuestion | null | undefined) || null,
    response: response?.response || '',
  };
}

function normalizeFeedbackWithResponses(feedback?: Feedback | null): Feedback | null {
  if (!feedback) {
    return null;
  }

  return {
    ...(feedback.created_at !== undefined && { created_at: feedback.created_at }),
    id: feedback.id,
    ...(feedback.is_published !== undefined && { is_published: feedback.is_published }),
    ...(feedback.origin_url !== undefined && { origin_url: feedback.origin_url }),
    responses: Array.isArray(feedback.responses) ? feedback.responses.map(normalizeFeedbackResponseEntry) : [],
    submitted_at: feedback.submitted_at || '',
    ...(feedback.status !== undefined && { status: feedback.status }),
    ...(feedback.time_spent_in_seconds !== undefined && { time_spent_in_seconds: feedback.time_spent_in_seconds }),
    ...(feedback.title !== undefined && { title: feedback.title }),
    ...(feedback.updated_at !== undefined && { updated_at: feedback.updated_at }),
  };
}

function normalizeFollowUpFeedback(
  feedback?: Feedback | Feedback[] | null,
): TicketManagerTicketDetail['follow_up_feedback'] {
  if (Array.isArray(feedback)) {
    return feedback.map(normalizeFeedbackWithResponses).filter((item): item is Feedback => Boolean(item));
  }

  return normalizeFeedbackWithResponses(feedback);
}

export function normalizeTicketManagerTicket(
  ticket: TicketManagerTicketApi | TicketManagerTicket,
): TicketManagerTicket {
  return {
    created_at: ticket.created_at,
    customer_name: 'customer_name' in ticket ? ticket.customer_name || '' : '',
    id: ticket.id,
    owner_name: normalizeTicketOwnerName(ticket),
    pool_id: 'pool_id' in ticket ? ticket.pool_id : ticket.ticket_pool_id || '',
    priority: ticket.priority,
    queue_id: 'queue_id' in ticket ? ticket.queue_id : ticket.ticket_queue_id || null,
    status: normalizeTicketManagerTicketStatus(ticket.status),
    subject: 'subject' in ticket ? ticket.subject : ticket.name || '',
    ticket_number: 'ticket_number' in ticket ? ticket.ticket_number : normalizeTicketNumber(ticket),
    ticket_no:
      'ticket_no' in ticket
        ? ticket.ticket_no
        : ticket.ticket_no || (ticket.ref_id !== undefined && ticket.ref_id !== null ? String(ticket.ref_id) : ''),
    updated_at: ticket.updated_at,
  };
}

export function normalizeTicketManagerTicketsResponse(
  response: ApiResponse<TicketManagerTicketApi[]> | ApiResponse<TicketManagerTicket[]>,
): ApiResponse<TicketManagerTicket[]> {
  return {
    ...response,
    data: (response.data || []).map((ticket) => normalizeTicketManagerTicket(ticket)),
  };
}

export function normalizeTicketManagerTicketDetail(
  ticket: TicketManagerTicketDetailApi | TicketManagerTicketDetail,
): TicketManagerTicketDetail {
  const rootCauses = Array.isArray(ticket.root_causes)
    ? ticket.root_causes.map((rootCause) => ({
        id: rootCause?.id || '',
        name: rootCause?.name || '',
      }))
    : [];
  const rootCauseNames = rootCauses
    .map((rootCause) => rootCause?.name)
    .filter(Boolean)
    .join(', ');

  return {
    assignee_id: normalizeTicketAssigneeId(ticket),
    assignee_name: normalizeTicketAssigneeName(ticket),
    created_at: ticket.created_at,
    custom_fields: Array.isArray(ticket.custom_fields) ? ticket.custom_fields : [],
    customer_feedback: normalizeFeedbackWithResponses(ticket.customer_feedback),
    customer: {
      id: ticket.customer?.id || '',
      name: ticket.customer?.name || '',
    },
    description: ticket.description || '',
    follow_up_feedback: normalizeFollowUpFeedback(ticket.follow_up_feedback),
    follow_up_survey:
      ticket.follow_up_survey?.url && ticket.follow_up_survey.flow_id
        ? {
            feedback_id: ticket.follow_up_survey.feedback_id || null,
            flow_id: ticket.follow_up_survey.flow_id,
            url: ticket.follow_up_survey.url,
          }
        : null,
    id: ticket.id,
    name: ticket.name || '',
    owner_id: normalizeTicketOwnerId(ticket),
    owner_name: normalizeTicketOwnerName(ticket),
    owner_type: normalizeTicketOwnerType(ticket),
    parent_ticket_id: ticket.parent_ticket_id || '-',
    permission_scope: {
      role: ticket.permission_scope?.role || '-',
    },
    priority: ticket.priority,
    ref_id:
      ticket.ref_id !== undefined && ticket.ref_id !== null && ticket.ref_id !== '' ? String(ticket.ref_id) : ticket.id,
    rootCauseGroupId: 'rootCauseGroupId' in ticket ? ticket.rootCauseGroupId : ticket.meta?.root_cause_group_id || '',
    root_causes: rootCauses,
    root_cause_names: rootCauseNames || '-',
    slug: ticket.slug || '',
    source_id: ticket.source_id || '',
    source_type: ticket.source_type || '',
    status: normalizeTicketManagerTicketStatus(ticket.status),
    ticket_number: normalizeTicketNumber(ticket),
    ticket_pool_id: ticket.ticket_pool_id || '',
    ticket_project_id: ticket.ticket_project_id || '',
    updated_at: ticket.updated_at,
  };
}
